import React, { CSSProperties, useState } from 'react';
import { Upload, Modal, Typography, Space, message } from 'antd';
import { UploadFile } from 'antd/lib/upload';
import { BoxArrowUpIcon, PlusIcon } from '@phosphor-icons/react';
import { DndContext, DragEndEvent, PointerSensor, useSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import {
  AssetUploaderClient,
  normalizeAssetUploadError,
  prepareAssetUploadFile,
} from '@admin-panel/providers';
import { AssetUsage } from '@admin-panel/types';
import * as Styles from './styles';
import Gallery from '../Gallery';

const { Text } = Typography;
type UploadRequestOption = Parameters<NonNullable<React.ComponentProps<typeof Upload>['customRequest']>>[0];
type UploadRequestFile = UploadRequestOption['file'];

interface MultiAssetUploaderProps {
  fileList?: Array<UploadFile>;
  onChange?: (info: { fileList: Array<UploadFile> }) => void;
  maxCount?: number;
  disabled?: boolean;
  className?: string;
  label?: string;
  enableDragDrop?: boolean;
  wrapperStyles?: CSSProperties;
}

const isImageFile = (file: UploadFile | File): boolean => {
  const imageExtensions = /\.(avif|jpg|jpeg|png|gif|webp|bmp|svg)$/i;
  const fileName = 'name' in file ? file.name : (file as UploadFile).name;
  const fileUrl = 'url' in file ? (file as UploadFile).url : undefined;
  const fileType = 'type' in file ? file.type : (file as UploadFile).type;

  return !!(
    fileUrl?.match(imageExtensions) ||
    fileName?.match(imageExtensions) ||
    fileType?.startsWith('image/')
  );
};

const isVideoFile = (file: UploadFile | File): boolean => {
  const videoExtensions = /\.(mp4|mov|avi|mkv|webm|m4v|3gp|flv|m3u8)$/i;
  const fileName = 'name' in file ? file.name : (file as UploadFile).name;
  const fileUrl = 'url' in file ? (file as UploadFile).url : undefined;
  const fileType = 'type' in file ? file.type : (file as UploadFile).type;

  return !!(
    fileUrl?.match(videoExtensions) ||
    fileName?.match(videoExtensions) ||
    fileType?.startsWith('video/')
  );
};

const getBrowserFile = (file: UploadRequestFile) => {
  if (file instanceof File) {
    return file;
  }

  throw new Error('Unsupported upload file type.');
};

const MultiAssetUploader = ({
  fileList = [],
  onChange,
  maxCount = 50, // Increased default limit
  disabled = false,
  className,
  label = 'Add Images & Videos',
  enableDragDrop = true,
  wrapperStyles = {},
}: MultiAssetUploaderProps) => {
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const client = new AssetUploaderClient();

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });

  // Separate files by type
  const imageFiles = fileList.filter(file => isImageFile(file));
  const videoFiles = fileList.filter(file => isVideoFile(file));

  const uploadFileToCDN = async ({ file, onError, onSuccess }: UploadRequestOption) => {
    const handleUploadError = (error: unknown) => {
      const uploadError = normalizeAssetUploadError(error);
      onError?.(uploadError);
      message.error(uploadError.message);
      setLoading(false);
    };

    try {
      setLoading(true);
      const browserFile = getBrowserFile(file);

      // Determine usage based on file type
      const usage: AssetUsage = isImageFile(browserFile) ? 'CATALOG' : 'VIDEO';
      const preparedFile = await prepareAssetUploadFile(browserFile);

      await client.uploadFile({
        file: preparedFile,
        usage: usage,
        onSuccess: (uploadedFile: File) => {
          onSuccess?.(uploadedFile);
          setLoading(false);
        },
        onError: handleUploadError,
        source: 'ASSET',
      });
    } catch (error: unknown) {
      handleUploadError(error);
    }
  };

  const handleUploadChange = (info: { fileList: Array<UploadFile> }) => {
    onChange?.({ fileList: info.fileList });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id && enableDragDrop) {
      const activeIndex = fileList.findIndex(file => file.uid === active.id);
      const overIndex = fileList.findIndex(file => file.uid === over?.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        const newFileList = arrayMove(fileList, activeIndex, overIndex);
        onChange?.({ fileList: newFileList });
      }
    }
  };

  const beforeUpload = async (file: File) => {
    const isImage = isImageFile(file);
    const isVideo = isVideoFile(file);

    if (!isImage && !isVideo) {
      message.error('You can only upload image or video files.');
      return Upload.LIST_IGNORE;
    }

    if (fileList.length >= maxCount) {
      message.error(`You can only upload up to ${maxCount} files total.`);
      return Upload.LIST_IGNORE;
    }

    if (!isImage) {
      return true;
    }

    try {
      return await prepareAssetUploadFile(file);
    } catch (error: unknown) {
      message.error(normalizeAssetUploadError(error).message);
      return Upload.LIST_IGNORE;
    }
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewVisible(true);
    const previewSource = file.url || '';
    const previewTitleFromUrl = previewSource
      ? previewSource.substring(previewSource.lastIndexOf('/') + 1)
      : '';

    setPreviewTitle(file.name || previewTitleFromUrl);
  };

  const handleCancel = () => setPreviewVisible(false);

  const canUploadMore = fileList.length < maxCount;

  const getUploadButton = () => {
    if (disabled || !canUploadMore) return null;

    if (enableDragDrop) {
      return (
        <Styles.DragUploadArea>
          <BoxArrowUpIcon size={48} />
          <Text>Click or drag files to upload</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Support for images and videos
          </Text>
        </Styles.DragUploadArea>
      );
    } else {
      return (
        <Styles.UploadButton>
          <PlusIcon />
          <Text type="secondary" style={{ fontSize: '12px', marginTop: 4 }}>
            {label}
          </Text>
        </Styles.UploadButton>
      );
    }
  };

  const UploaderComponent = enableDragDrop ? Upload.Dragger : Upload;

  return (
    <div className={className} style={wrapperStyles}>
      <Styles.Container>
        {label ? (
          <Text strong style={{ marginBottom: 16, display: 'block' }}>
            {label}
          </Text>
        ) : null}

        <DndContext sensors={[sensor]} onDragEnd={onDragEnd}>
          <SortableContext items={fileList.map(file => file.uid)} strategy={rectSortingStrategy}>
            <Styles.ContentWrapper>
              {canUploadMore && (
                <Styles.UploadWrapper>
                  <UploaderComponent
                    multiple={true}
                    fileList={fileList}
                    onChange={handleUploadChange}
                    customRequest={uploadFileToCDN}
                    beforeUpload={beforeUpload}
                    accept="image/*,video/*"
                    showUploadList={false}
                    disabled={disabled || loading}
                    onPreview={handlePreview}
                  >
                    {getUploadButton()}
                  </UploaderComponent>
                </Styles.UploadWrapper>
              )}

              {fileList.length > 0 && (
                <Styles.GalleryWrapper>
                  <Gallery
                    fileList={fileList}
                    setFileList={newFileList => onChange?.({ fileList: newFileList })}
                    magnificationFactor={40}
                  />
                </Styles.GalleryWrapper>
              )}
            </Styles.ContentWrapper>
          </SortableContext>
        </DndContext>

        <Styles.FileCount>
          <Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Images: {imageFiles.length}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Videos: {videoFiles.length}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Total: {fileList.length}
            </Text>
          </Space>
        </Styles.FileCount>
      </Styles.Container>

      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
        width="80%"
        style={{ top: 20 }}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

// Helper function for base64 conversion
const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });

export default MultiAssetUploader;
